// src/Editor.ts
import { Editor as CoreEditor } from "@tiptap/core";
var Editor = class extends CoreEditor {
  constructor() {
    super(...arguments);
    this.contentComponent = null;
  }
};

// src/EditorContent.ts
var EditorContent = {
  name: "EditorContent",
  props: {
    editor: {
      default: null,
      type: Object
    }
  },
  watch: {
    editor: {
      immediate: true,
      handler(editor) {
        if (editor && editor.options.element) {
          this.$nextTick(() => {
            var _a;
            const element = this.$el;
            if (!element || !((_a = editor.view.dom) == null ? void 0 : _a.firstChild)) {
              return;
            }
            element.append(editor.view.dom);
            editor.contentComponent = this;
            editor.setOptions({
              element
            });
            editor.createNodeViews();
          });
        }
      }
    }
  },
  render(createElement) {
    return createElement("div");
  },
  beforeDestroy() {
    var _a;
    const { editor } = this;
    if (!editor) {
      return;
    }
    if (!editor.isDestroyed) {
      editor.view.setProps({
        nodeViews: {}
      });
    }
    editor.contentComponent = null;
    if (!((_a = editor.view.dom) == null ? void 0 : _a.firstChild)) {
      return;
    }
    const newElement = document.createElement("div");
    newElement.append(editor.view.dom);
    editor.setOptions({
      element: newElement
    });
  }
};

// src/NodeViewContent.ts
var NodeViewContent = {
  props: {
    as: {
      type: String,
      default: "div"
    }
  },
  render(createElement) {
    return createElement(this.as, {
      style: {
        whiteSpace: "pre-wrap"
      },
      attrs: {
        "data-node-view-content": ""
      }
    });
  }
};

// src/NodeViewWrapper.ts
var NodeViewWrapper = {
  props: {
    as: {
      type: String,
      default: "div"
    }
  },
  inject: ["onDragStart", "decorationClasses"],
  render(createElement) {
    return createElement(
      this.as,
      {
        class: this.decorationClasses.value,
        style: {
          whiteSpace: "normal"
        },
        attrs: {
          "data-node-view-wrapper": ""
        },
        on: {
          dragstart: this.onDragStart
        }
      },
      this.$slots.default
    );
  }
};

// src/VueNodeViewRenderer.ts
import { NodeView } from "@tiptap/core";
import { booleanProp, functionProp, objectProp } from "vue-ts-types";

// src/Vue.ts
import VueDefault from "vue";
var Vue = VueDefault;

// src/VueRenderer.ts
var VueRenderer = class {
  constructor(component, props) {
    const Component = typeof component === "function" ? component : Vue.extend(component);
    this.ref = new Component(props).$mount();
  }
  get element() {
    return this.ref.$el;
  }
  updateProps(props = {}) {
    var _a, _b, _c;
    if (!this.ref.$props) {
      return;
    }
    const currentVueConstructor = (_c = (_b = (_a = this.ref.$props.editor) == null ? void 0 : _a.contentComponent) == null ? void 0 : _b.$options._base) != null ? _c : Vue;
    const originalSilent = currentVueConstructor.config.silent;
    currentVueConstructor.config.silent = true;
    Object.entries(props).forEach(([key, value]) => {
      this.ref.$props[key] = value;
    });
    currentVueConstructor.config.silent = originalSilent;
  }
  destroy() {
    this.ref.$destroy();
  }
};

// src/VueNodeViewRenderer.ts
var nodeViewProps = {
  editor: objectProp().required,
  node: objectProp().required,
  decorations: objectProp().required,
  selected: booleanProp().required,
  extension: objectProp().required,
  getPos: functionProp().required,
  updateAttributes: functionProp().required,
  deleteNode: functionProp().required
};
var VueNodeView = class extends NodeView {
  mount() {
    var _a, _b;
    const props = {
      editor: this.editor,
      node: this.node,
      decorations: this.decorations,
      innerDecorations: this.innerDecorations,
      view: this.view,
      selected: false,
      extension: this.extension,
      HTMLAttributes: this.HTMLAttributes,
      getPos: () => this.getPos(),
      updateAttributes: (attributes = {}) => this.updateAttributes(attributes),
      deleteNode: () => this.deleteNode()
    };
    const onDragStart = this.onDragStart.bind(this);
    this.decorationClasses = Vue.observable({
      value: this.getDecorationClasses()
    });
    const vue = (_b = (_a = this.editor.contentComponent) == null ? void 0 : _a.$options._base) != null ? _b : Vue;
    const Component = vue.extend(this.component).extend({
      props: Object.keys(props),
      provide: () => {
        return {
          onDragStart,
          decorationClasses: this.decorationClasses
        };
      }
    });
    this.handleSelectionUpdate = this.handleSelectionUpdate.bind(this);
    this.editor.on("selectionUpdate", this.handleSelectionUpdate);
    this.renderer = new VueRenderer(Component, {
      parent: this.editor.contentComponent,
      propsData: props
    });
  }
  /**
   * Return the DOM element.
   * This is the element that will be used to display the node view.
   */
  get dom() {
    if (!this.renderer.element.hasAttribute("data-node-view-wrapper")) {
      throw Error("Please use the NodeViewWrapper component for your node view.");
    }
    return this.renderer.element;
  }
  /**
   * Return the content DOM element.
   * This is the element that will be used to display the rich-text content of the node.
   */
  get contentDOM() {
    if (this.node.isLeaf) {
      return null;
    }
    return this.dom.querySelector("[data-node-view-content]");
  }
  /**
   * On editor selection update, check if the node is selected.
   * If it is, call `selectNode`, otherwise call `deselectNode`.
   */
  handleSelectionUpdate() {
    const { from, to } = this.editor.state.selection;
    const pos = this.getPos();
    if (typeof pos !== "number") {
      return;
    }
    if (from <= pos && to >= pos + this.node.nodeSize) {
      if (this.renderer.ref.$props.selected) {
        return;
      }
      this.selectNode();
    } else {
      if (!this.renderer.ref.$props.selected) {
        return;
      }
      this.deselectNode();
    }
  }
  /**
   * On update, update the React component.
   * To prevent unnecessary updates, the `update` option can be used.
   */
  update(node, decorations, innerDecorations) {
    const rerenderComponent = (props) => {
      this.decorationClasses.value = this.getDecorationClasses();
      this.renderer.updateProps(props);
    };
    if (typeof this.options.update === "function") {
      const oldNode = this.node;
      const oldDecorations = this.decorations;
      const oldInnerDecorations = this.innerDecorations;
      this.node = node;
      this.decorations = decorations;
      this.innerDecorations = innerDecorations;
      return this.options.update({
        oldNode,
        oldDecorations,
        newNode: node,
        newDecorations: decorations,
        oldInnerDecorations,
        innerDecorations,
        updateProps: () => rerenderComponent({ node, decorations, innerDecorations })
      });
    }
    if (node.type !== this.node.type) {
      return false;
    }
    if (node === this.node && this.decorations === decorations && this.innerDecorations === innerDecorations) {
      return true;
    }
    this.node = node;
    this.decorations = decorations;
    this.innerDecorations = innerDecorations;
    rerenderComponent({ node, decorations, innerDecorations });
    return true;
  }
  /**
   * Select the node.
   * Add the `selected` prop and the `ProseMirror-selectednode` class.
   */
  selectNode() {
    this.renderer.updateProps({
      selected: true
    });
    this.renderer.element.classList.add("ProseMirror-selectednode");
  }
  /**
   * Deselect the node.
   * Remove the `selected` prop and the `ProseMirror-selectednode` class.
   */
  deselectNode() {
    this.renderer.updateProps({
      selected: false
    });
    this.renderer.element.classList.remove("ProseMirror-selectednode");
  }
  getDecorationClasses() {
    return this.decorations.flatMap((item) => item.type.attrs.class).join(" ");
  }
  destroy() {
    this.renderer.destroy();
    this.editor.off("selectionUpdate", this.handleSelectionUpdate);
  }
};
function VueNodeViewRenderer(component, options) {
  return (props) => {
    if (!props.editor.contentComponent) {
      return {};
    }
    return new VueNodeView(component, props, options);
  };
}

// src/index.ts
export * from "@tiptap/core";
export {
  Editor,
  EditorContent,
  NodeViewContent,
  NodeViewWrapper,
  VueNodeViewRenderer,
  VueRenderer,
  nodeViewProps
};
//# sourceMappingURL=index.js.map